//@flow

import {useState, useEffect} from 'react';
import {
  listCrmCampaignsService,
  createCrmCampaignService,
  editCrmCampaignService,
  deleteCrmCampaignService,
  getCrmCampaignByIdService,
  getCampaignExecutionService,
} from '../promises';
import type {
  CreateCampaignWebRequest,
  EditCampaignWebRequest,
} from '../promises';

import type {
  CrmCampaign,
  CrmCampaignExecution,
} from '../../../entities/crm/campaign';
import type {Pagination} from '../../../entities';

const defaultPagination: Pagination = {
  page: 0,
  size: 8,
  total: 0,
};

export function useCrmCampaigns() {
  const [campaigns, setCampaigns] = useState<CrmCampaign[]>([]);

  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(8);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    listCrmCampaignsService({
      page,
      size,
    })
      .then(response => {
        setCampaigns(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, size]);

  return {
    crmCampaigns: campaigns,
    loading,
    error,
    pagination,
    setSize,
    setPage,
  };
}

export function useCrmCampaignsActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createCrmCampaign = (request: CreateCampaignWebRequest) => {
    setLoading(true);
    return createCrmCampaignService(request)
      .then(response => {
        return response;
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editCrmCampaign = (
    campaignId: string,
    request: EditCampaignWebRequest
  ) => {
    setLoading(true);
    return editCrmCampaignService(campaignId, request)
      .then(response => {
        return response;
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteCrmCampaign = (campaignId: string) => {
    setLoading(true);
    return deleteCrmCampaignService(campaignId)
      .then(() => {
        return;
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    deleteCrmCampaign,
    createCrmCampaign,
    editCrmCampaign,
    loading,
    error,
  };
}

export function useCrmCampaign(campaignId?: string | null) {
  const [campaign, setCampaign] = useState<CrmCampaign | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaignId) {
      setCampaign(null);
      return;
    }
    setLoading(true);
    getCrmCampaignByIdService(campaignId)
      .then(response => {
        setCampaign(response);
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [campaignId]);

  return {
    campaign,
    loading,
    error,
  };
}

export function useCrmCampaignExecution(campaignId?: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [executions, setExecutions] = useState<CrmCampaignExecution[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);

  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    getCampaignExecutionService(campaignId, {
      page,
      size: 10,
    })
      .then(response => {
        setExecutions(response.data ?? []);
        if (response.pagination) setPagination(response.pagination);
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [campaignId, page]);

  return {
    executions,
    page,
    setPage,
    pagination,
    loading,
    error,
  };
}
